// ============================================================================
// Driver performance — the two things this page can say about a driver's day:
//
//   1. REPORTING PUNCTUALITY. Did the vehicle reach the warehouse before the
//      morning cutoff? Arrival at or after 08:00 IST is LATE, before it ON TIME.
//      Judged in IST explicitly, never the browser's zone — an operator opening
//      this from another timezone must reach the same verdict as the desk in
//      Bengaluru, and "before 8 AM" means 8 AM there.
//
//   2. DELIVERY SLA. How many of the shipments they carried breached, per the
//      rules in `breach.ts` — and, kept separately, how many missed their cutoff
//      for a reason that is not the transporter's (`excused`). Those are counted
//      but never charged to the driver: folding them into the on-time half would
//      tell a driver they were punctual on a drop that landed three hours late.
//
// Counted PER DRIVER, not per shipment: a driver running twelve drops arrived
// once, and counting their twelve rows would drown out a driver running two. The
// breach count is the exception — that one IS per shipment, because it is asking
// how much of their load went late.
//
// OVER A MULTI-DAY FILTER, punctuality is counted in MORNINGS, not shown as a
// clock time. A driver who ran five days has five arrivals, and there is no honest
// way to print them as one: an average would land on a time none of those mornings
// actually was, and would let two 6 AM starts cancel out a 10 AM one. So the page
// judges each day against that day's own cutoff and reads the tally — "3 of 5 days
// on time". A day Loginext pushed no check-in for is not in the denominator at all:
// a missing callback is not a morning the driver missed.
//
// THE ARRIVAL TIME IS LOGINEXT'S FIRST MILESTONE CHECK-IN ON THE ROUTE. Their
// CHECKINPUSHNOTIFICATION webhook fires when a driver checks in at a stop, carrying
// `checkInTime`; the EARLIEST of those across the driver's run is when their day
// started, whichever kind of stop it happened to be. The server folds that minimum
// per driver and this file turns it into a verdict; a driver Loginext pushed no
// check-in at all for reads "—".
// ============================================================================
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";

// 08:00 IST. One edit moves the rule and every label derived from it.
export const ARRIVAL_CUTOFF_HOUR = 8;
export const ARRIVAL_CUTOFF_LABEL = "8:00 AM IST";

export type ArrivalStatus = "on_time" | "late";

// on_time / late for one arrival, or null when we have no arrival to judge.
export const arrivalStatus = (arrivedAt: string | null | undefined): ArrivalStatus | null => {
  if (!arrivedAt) return null;
  const parsed = dayjs.utc(arrivedAt);
  if (!parsed.isValid()) return null;
  const ist = parsed.tz(IST);
  // The cutoff is 08:00 on the SAME IST day the vehicle arrived — not a fixed instant, so a trip
  // running past midnight is judged against its own morning.
  const cutoff = ist.hour(ARRIVAL_CUTOFF_HOUR).minute(0).second(0).millisecond(0);
  return ist.isBefore(cutoff) ? "on_time" : "late";
};

// One morning of one driver's range: the IST calendar day, and their earliest check-in on it.
export interface DriverDay {
  day: string; // YYYY-MM-DD, the IST day the server bucketed the check-in into
  arrivedAt: string; // ISO — the earliest milestone check-in on that day
  status: ArrivalStatus; // judged against 08:00 IST on `day` itself, never a fixed instant
}

export interface DriverPerformance {
  driver: string;
  vehicle: string | null;
  vendor: string | null;
  trips: string[]; // the trip name(s) this driver is running in the current scope
  shipments: number; // how many shipments they carried in scope
  breaches: number; // …how many of those breached the delivery SLA
  excused: number; // …and how many missed their cutoff on an escalation that excuses it
  // Earliest milestone check-in Loginext pushed for this driver, ISO — null when none landed.
  // Over a one-day filter this IS the day's arrival; over a longer one it is the earliest of the
  // range, which is why a multi-day view reads `days` instead of printing this.
  arrivedAt: string | null;
  status: ArrivalStatus | null;
  // One entry per day this driver checked in, oldest first. Empty when Loginext pushed nothing.
  days: DriverDay[];
}

// How many of this driver's mornings beat the cutoff, and how many we can judge at all.
export const dayTally = (d: DriverPerformance) => ({
  onTime: d.days.filter((x) => x.status === "on_time").length,
  judged: d.days.length,
});

// Wired: `arrivedAt` is the earliest `checkInTime` across the driver's CHECKINPUSHNOTIFICATIONs,
// folded per driver in `carrier_board.driver_performance` and turned into a verdict by
// `arrivalStatus` above. A driver with no check-in at all reads null, and the column shows a dash.
export const ARRIVAL_DATA_AVAILABLE = true;

// `driversFromRows` moved to the server (oms/logistics/services/carrier_board.py):
// the roll-up spans the whole filtered range, which the browser could only do while it
// held every row — and holding every row is what stopped this page paginating.

export const driverTotals = (drivers: DriverPerformance[]) => ({
  onTime: ARRIVAL_DATA_AVAILABLE
    ? drivers.filter((d) => d.status === "on_time").length
    : null,
  total: drivers.length,
  // The multi-day pair: DRIVER-DAYS, not drivers. Five drivers over three days is up to fifteen
  // mornings, and the tile says how many of them started before the cutoff. Counted off the days
  // we actually heard about, so a day of missing webhooks shrinks both halves rather than reading
  // as a day everyone was late.
  onTimeDays: ARRIVAL_DATA_AVAILABLE
    ? drivers.reduce((n, d) => n + dayTally(d).onTime, 0)
    : null,
  judgedDays: drivers.reduce((n, d) => n + d.days.length, 0),
  breaches: drivers.reduce((n, d) => n + d.breaches, 0),
  excused: drivers.reduce((n, d) => n + d.excused, 0),
  shipments: drivers.reduce((n, d) => n + d.shipments, 0),
  driversWithBreaches: drivers.filter((d) => d.breaches > 0).length,
});
